/**
 * Signup Promo - Customer Registration Modal
 * Opens modal for customer signup, reveals discount code on success.
 * Uses contact[email] for newsletter subscription (works with new customer accounts).
 * Additional data (name, phone) is compiled into contact[body] for store inbox.
 * Handles discount conflict with MAREA30 (White Friday).
 */

class SignupPromo {
  constructor() {
    this.section = document.querySelector('[data-section-id]');
    this.modal = document.querySelector('[data-signup-promo-modal]');
    this.trigger = document.querySelector('[data-signup-promo-trigger]');

    if (!this.modal || !this.trigger) return;

    this.overlay = this.modal.querySelector('[data-signup-promo-close]');
    this.closeButtons = this.modal.querySelectorAll('[data-signup-promo-close]');
    this.formState = this.modal.querySelector('[data-signup-form-state]');
    this.successState = this.modal.querySelector('[data-signup-success-state]');
    this.form = this.modal.querySelector('form');
    this.shopNowButton = this.modal.querySelector('[data-signup-shop-now]');

    // Form field references for customer note and contact body compilation
    this.firstNameInput = this.modal.querySelector('[data-signup-firstname]');
    this.lastNameInput = this.modal.querySelector('[data-signup-lastname]');
    this.phoneInput = this.modal.querySelector('[data-signup-phone]');
    this.noteField = this.modal.querySelector('input[name="customer[note]"]');
    this.bodyField = this.modal.querySelector('input[name="contact[body]"]');

    this.STORAGE_KEY = 'marea_signup_promo_completed';
    this.PENDING_KEY = 'marea_signup_pending';
    this.DISCOUNT_STORAGE_KEY = 'marea_active_discount';
    this.previousActiveElement = null;

    // Make instance globally available for Liquid success detection
    window.SignupPromoInstance = this;

    this.init();
  }

  init() {
    // Check if user already signed up
    if (this.wasAlreadyCompleted()) {
      this.hideSection();
      return;
    }

    // Always bind events first
    this.bindEvents();

    // Check if form was just submitted (localStorage flag from before redirect)
    if (this.checkPendingSuccess()) {
      this.clearPendingSuccess();
      this.showSuccess();
      this.openModal();
      return;
    }

    // Check if form was just submitted successfully (page reload scenario)
    if (this.checkFormSuccess()) {
      this.showSuccess();
      this.openModal();
    }
  }

  wasAlreadyCompleted() {
    try {
      return localStorage.getItem(this.STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  markAsCompleted() {
    try {
      localStorage.setItem(this.STORAGE_KEY, 'true');
    } catch (e) {
      // Ignore if localStorage is unavailable
    }
  }

  setPendingSuccess() {
    try {
      localStorage.setItem(this.PENDING_KEY, 'true');
    } catch (e) {
      // Ignore if localStorage is unavailable
    }
  }

  checkPendingSuccess() {
    try {
      return localStorage.getItem(this.PENDING_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  clearPendingSuccess() {
    try {
      localStorage.removeItem(this.PENDING_KEY);
    } catch (e) {
      // Ignore if localStorage is unavailable
    }
  }

  hideSection() {
    if (this.section) {
      this.section.style.display = 'none';
    }
  }

  checkFormSuccess() {
    // Check if the page has form success indicator
    const successScript = this.modal.querySelector('[data-signup-form-state] script');
    return successScript !== null;
  }

  bindEvents() {
    // Open modal
    this.trigger.addEventListener('click', () => this.openModal());

    // Close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });

    // Escape key
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Shop Now button - apply smart discount
    if (this.shopNowButton) {
      this.shopNowButton.addEventListener('click', (e) => this.handleShopNow(e));
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', () => this.handleFormSubmit());
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeModal();
      return;
    }

    // Tab trap
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  getFocusableElements() {
    const focusable = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    return Array.from(focusable).filter(el => !el.disabled && el.offsetParent !== null);
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  openModal() {
    if (!this.modal) return;

    // Store current focus
    this.previousActiveElement = document.activeElement;

    // Show modal
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('signup-promo-modal-open');

    // Add keyboard listener
    document.addEventListener('keydown', this.handleKeyDown);

    // Focus first focusable element
    setTimeout(() => {
      const focusable = this.getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    }, 100);
  }

  closeModal() {
    if (!this.modal) return;

    // Hide modal
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('signup-promo-modal-open');

    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeyDown);

    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  handleFormSubmit() {
    // Compile customer data into note and body fields before submission
    this.compileCustomerData();

    // Set pending flag before form submits (page will redirect)
    // On next page load, we'll check this flag and show success modal
    this.setPendingSuccess();
  }

  compileCustomerData() {
    const firstName = this.firstNameInput?.value?.trim() || '';
    const lastName = this.lastNameInput?.value?.trim() || '';
    const phone = this.phoneInput?.value?.trim() || '';

    // Format data for customer note (saved directly to customer record)
    if (this.noteField) {
      const noteParts = [];
      noteParts.push('MAREA10 Signup Promo');
      if (firstName || lastName) {
        noteParts.push(`Name: ${firstName} ${lastName}`.trim());
      }
      if (phone) {
        noteParts.push(`Phone: ${phone}`);
      }
      noteParts.push(`Date: ${new Date().toLocaleDateString()}`);
      this.noteField.value = noteParts.join(' | ');
    }

    // Also send to contact body as backup (goes to store inbox)
    if (this.bodyField) {
      const bodyParts = [];
      bodyParts.push('=== MAREA10 Signup Promo ===');
      if (firstName || lastName) {
        bodyParts.push(`Name: ${firstName} ${lastName}`.trim());
      }
      if (phone) {
        bodyParts.push(`Phone: ${phone}`);
      }
      bodyParts.push(`Date: ${new Date().toLocaleDateString()}`);
      bodyParts.push('');
      bodyParts.push('Customer signed up for 10% discount via signup promo banner.');
      this.bodyField.value = bodyParts.join('\n');
    }
  }

  showSuccess() {
    if (!this.formState || !this.successState) return;

    // Hide form state, show success state
    this.formState.classList.remove('is-active');
    this.successState.classList.add('is-active');

    // Mark as completed
    this.markAsCompleted();

    // Update shop now URL with smart discount logic
    this.updateShopNowUrl();
  }

  getSmartDiscountUrl() {
    try {
      // Check if MAREA30 (30% White Friday discount) is already active
      const activeDiscount = localStorage.getItem(this.DISCOUNT_STORAGE_KEY);
      if (activeDiscount === 'MAREA30') {
        // Don't replace 30% with 10%, just redirect to collection
        return '/collections/all';
      }
    } catch (e) {
      // Ignore localStorage errors
    }

    // Apply MAREA10 discount
    return '/discount/MAREA10?redirect=/collections/all';
  }

  updateShopNowUrl() {
    if (this.shopNowButton) {
      this.shopNowButton.href = this.getSmartDiscountUrl();
    }
  }

  handleShopNow(event) {
    // Update URL in case it wasn't already updated
    const smartUrl = this.getSmartDiscountUrl();
    this.shopNowButton.href = smartUrl;

    // Close modal after a brief delay to allow navigation
    setTimeout(() => {
      this.closeModal();
      this.hideSection();
    }, 100);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SignupPromo());
} else {
  new SignupPromo();
}
